/**
 * Catálogo de logs Loki por servicio.
 * Solo jobs que Promtail scrapea (docs/grafana/03-promtail-y-labels.md).
 * rabbitmq-ha / nodos / vultr / admin NO tienen scrape → no inventar links vacíos.
 *
 * Enlaces a Grafana Explore: desactivados por ahora (queries vacías / Explore
 * confuso). Usar tail K8s en el drilldown. Reactivar con ENABLE_GRAFANA_LOG_LINKS.
 */

/** Off hasta que Loki/Explore aporte señales útiles en el checklist matutino. */
export const ENABLE_GRAFANA_LOG_LINKS = false

/** Jobs con datos reales en Loki (label `job`). */
export const LOKI_SCRAPED_JOBS = new Set([
  'smart-tech-api',
  'capitaria-worker',
  'banking-worker',
  'document-worker',
  'export-worker',
  'floid-worker',
  'pasarelas-worker',
  'portal-mcp-worker',
  'public-api-worker',
  'scrappers-worker',
  'sii-worker',
  'transbank-worker',
  'mailing',
  'conciliacion-microservice',
  'buk-microservice',
  'match-recommender-microservice',
  'rule-miner-microservice',
  'reconcile-heuristica-api',
  'reconcile-llm-api',
])

/** Cola Rabbit → worker que la consume (job Loki). */
export const QUEUE_TO_LOG_JOB = {
  'email-queue': 'mailing',
  'email-priority-queue': 'mailing',
  'sii-queue': 'sii-worker',
  'sii-priority-queue': 'sii-worker',
  'sii-resume-queue': 'sii-worker',
  'tax-documents-queue': 'document-worker',
  'create-document-queue': 'document-worker',
  'create-document-cron-queue': 'document-worker',
  'create-document-cedidos-queue': 'document-worker',
  'create-document-cedidos-cron-queue': 'document-worker',
  'create-sii-resume-queue': 'document-worker',
  'create-sii-resume-priority-queue': 'sii-worker',
  'create-bsale-queue': 'document-worker',
  'create-bsale-cron-queue': 'document-worker',
}

/** @type {Record<string, { job: string, hint: string }>} */
export const PORTAL_LOG_CATALOG = {
  'smart-tech-api': {
    job: 'smart-tech-api',
    hint: 'Logs del monolito (GraphQL, login, jobs).',
  },
  'portal-mcp-worker': {
    job: 'portal-mcp-worker',
    hint: 'Logs del connector MCP / OAuth.',
  },
  'public-api-worker': {
    job: 'public-api-worker',
    hint: 'Logs de la API pública (tools MCP).',
  },
  mailing: {
    job: 'mailing',
    hint: 'Logs del worker de correo.',
  },
  'document-worker': {
    job: 'document-worker',
    hint: 'Logs de documentos / Bsale / tax-documents.',
  },
  'sii-worker': {
    job: 'sii-worker',
    hint: 'Logs SII (proxy, scraping, colas sii-*).',
  },
}

const GRAFANA_BASE = 'https://grafana.smartcfo.cl'

/**
 * Deep-link a Grafana Explore (Loki).
 * Formato panes (Grafana 10+) + left legacy por compatibilidad.
 * @param {string} expr LogQL completo
 */
export function buildLokiExploreUrl(expr) {
  const datasource = { type: 'loki', uid: 'loki' }
  const query = {
    refId: 'A',
    expr,
    queryType: 'range',
    datasource,
    editorMode: 'code',
  }
  const range = { from: 'now-1h', to: 'now' }
  const paneId = 'ph'
  const panes = {
    [paneId]: {
      datasource: 'loki',
      queries: [query],
      range,
    },
  }
  // left: [from, to, datasource, queries..., ui]
  const left = JSON.stringify([
    'now-1h',
    'now',
    'loki',
    { datasource: 'loki', queries: [query], range },
  ])
  const params = new URLSearchParams({
    schemaVersion: '1',
    panes: JSON.stringify(panes),
    orgId: '1',
    left,
  })
  return `${GRAFANA_BASE}/explore?${params.toString()}`
}

/**
 * @param {string} job
 * @returns {boolean}
 */
export function isLokiScrapedJob(job) {
  return Boolean(job && LOKI_SCRAPED_JOBS.has(job))
}

/**
 * Resuelve job Loki para un nombre de cola o deployment.
 * @param {string} name
 * @returns {string | null}
 */
export function resolveLogJob(name) {
  if (!name) return null
  if (QUEUE_TO_LOG_JOB[name]) return QUEUE_TO_LOG_JOB[name]
  if (isLokiScrapedJob(name)) return name
  return null
}

/**
 * @param {string} job
 * @returns {{ url: string, hint: string, allUrl?: string, criticalUrl?: null, job: string } | null}
 */
export function getServiceLogLinks(job) {
  if (!ENABLE_GRAFANA_LOG_LINKS) return null
  const resolved = resolveLogJob(job)
  if (!resolved) return null
  const entry = PORTAL_LOG_CATALOG[resolved]
  const hint = entry?.hint || `Logs Loki · job=${resolved}`
  return {
    url: buildLokiExploreUrl(`{job="${resolved}"}`),
    // aliases for callers still using allUrl
    allUrl: buildLokiExploreUrl(`{job="${resolved}"}`),
    criticalUrl: null,
    hint,
    job: resolved,
  }
}

/** Regex client-side para filtrar tail K8s. */
export const CRITICAL_LOG_LINE_RE =
  /\b(ERROR|CRIT|FATAL|WARN|WARNING|panic|Erlang error|alarm|OOM|ECONNREFUSED|exception|crash)\b/i

/**
 * @param {string} line
 * @returns {boolean}
 */
export function isCriticalLogLine(line) {
  return CRITICAL_LOG_LINE_RE.test(line || '')
}
